import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { AsyncListShell } from "@/components/admin/AsyncListShell";
import { LeadPeriodControls, LeadPeriodCounter, TipoUnidadSelect, LEAD_FILTER_INITIAL } from "@/components/admin/LeadPeriodFilter";
import { formatIngreso, inPeriod, periodRange, tipoOf } from "@/lib/leadPeriod";
import { CATEGORY_LABELS, CATEGORY_KEY_TO_SLUG } from "@/lib/catalog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const ESTADOS = ["Nuevo", "Contactado", "Cerrado"];
const ESTADO_COLORS = {
  "Nuevo": "bg-[#C4674F]/15 text-[#A0553F]",
  "Contactado": "bg-[#F1F3F3] text-[#3E5641]",
  "Cerrado": "bg-[#A0553F] text-white",
};

export default function CatalogInterestsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("all");
  const [editing, setEditing] = useState(null);
  const [lf, setLf] = useState(LEAD_FILTER_INITIAL);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/catalog/interests");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const range = periodRange(lf.period, lf.desde, lf.hasta);
  const filtered = useMemo(() => items.filter((it) => {
    if (estado !== "all" && it.estado !== estado) return false;
    if (!inPeriod(it.created_at, range)) return false;
    if (lf.tipo !== "all" && tipoOf(it) !== lf.tipo) return false;
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return [it.nombre, it.telefono, it.correo, it.unit_titulo, it.category].some((v) => String(v || "").toLowerCase().includes(t));
  }), [items, q, estado, lf]); // eslint-disable-line react-hooks/exhaustive-deps

  async function onDelete(id) {
    if (!window.confirm("¿Eliminar este lead de interés?")) return;
    try { await api.delete(`/catalog/interests/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div data-testid="admin-catalog-interests">
      <header className="mb-8">
        <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">Módulo 08</p>
        <h1 className="mt-1 font-serif text-3xl text-[#3E5641] md:text-4xl">Interesados en Inventario</h1>
        <p className="mt-1 text-sm text-[#6B7678]">
          Leads del botón "Me interesa" desde las páginas públicas de Nuevos, Seminuevos y Promociones.
        </p>
      </header>

      <div className="mb-4 flex flex-wrap gap-3">
        <Input placeholder="Buscar por nombre, unidad, categoría…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" data-testid="int-search" />
        <Select value={estado} onValueChange={setEstado}>
          <SelectTrigger className="w-52" data-testid="int-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {ESTADOS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
          </SelectContent>
        </Select>
        <LeadPeriodControls value={lf} onChange={setLf} prefix="int" />
      </div>
      {!loading && <LeadPeriodCounter rows={filtered} prefix="int" />}

      <AsyncListShell
        loading={loading}
        isEmpty={filtered.length === 0}
        loadingTestId="int-loading"
        emptyTestId="int-empty"
        emptyLabel="Sin interesados aún."
      >
        <div className="overflow-hidden rounded-sm border border-[#3E5641]/10 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F1F3F3] text-left text-xs uppercase tracking-wider text-[#6B7678]">
                <tr>
                  <th className="px-4 py-3">Ingreso</th>
                  <th className="px-4 py-3">Nombre</th>
                  <th className="px-4 py-3">Contacto</th>
                  <th className="px-4 py-3">Unidad</th>
                  <th className="px-4 py-3">Categoría</th>
                  <th className="px-4 py-3">Tipo</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#3E5641]/10">
                {filtered.map((it) => (
                  <tr key={it.id} className="hover:bg-[#F1F3F3]/50" data-testid={`int-row-${it.id}`}>
                    <td className="px-4 py-3 text-xs whitespace-nowrap text-[#6B7678]" data-testid={`int-ingreso-${it.id}`}>{formatIngreso(it.created_at)}</td>
                    <td className="px-4 py-3 font-medium text-[#3E5641]">{it.nombre}</td>
                    <td className="px-4 py-3">
                      <div>{it.telefono}</div>
                      <div className="text-xs text-[#6B7678]">{it.correo}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <Link to={`/inventario/${CATEGORY_KEY_TO_SLUG[it.category] || it.category}/${it.unit_id}`} target="_blank" className="text-[#A0553F] hover:underline">
                        {it.unit_titulo}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-xs">{CATEGORY_LABELS[it.category] || it.category}</td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap" data-testid={`int-tipo-${it.id}`}>{tipoOf(it)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block rounded-sm px-2 py-1 text-xs ${ESTADO_COLORS[it.estado] || ""}`}>{it.estado}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setEditing(it)} className="mr-2 p-1 hover:bg-[#F1F3F3]" data-testid={`int-edit-${it.id}`}><Pencil size={14} /></button>
                      <button onClick={() => onDelete(it.id)} className="p-1 text-red-600 hover:bg-red-50" data-testid={`int-delete-${it.id}`}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </AsyncListShell>

      {editing && <InterestDialog key={editing.id} item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function InterestDialog({ item, onClose, onSaved }) {
  const [form, setForm] = useState({
    nombre: item.nombre, telefono: item.telefono, correo: item.correo,
    interes: item.interes, estado: item.estado, notas: item.notas || "", tipo_unidad: tipoOf(item),
  });
  const [saving, setSaving] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    setSaving(true);
    try {
      await api.patch(`/catalog/interests/${item.id}`, {
        nombre: form.nombre.trim(),
        telefono: form.telefono.trim(),
        correo: form.correo.trim(),
        interes: form.interes.trim(),
        estado: form.estado,
        notas: form.notas?.trim() || null,
        tipo_unidad: form.tipo_unidad,
      });
      toast.success("Lead actualizado");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-serif text-2xl">{item.nombre}</DialogTitle></DialogHeader>
        <p className="text-sm text-[#6B7678]">
          Interés original: <span className="mono text-[#3E5641]">{item.unit_titulo}</span> · {CATEGORY_LABELS[item.category] || item.category}
        </p>
        <p className="text-sm text-[#6B7678]">Fecha y hora de ingreso: <span className="font-medium text-[#3E5641]" data-testid="int-detail-ingreso">{formatIngreso(item.created_at)}</span></p>
        <div className="grid gap-4 md:grid-cols-2">
          <F label="Nombre"><Input value={form.nombre} onChange={(e) => setF("nombre", e.target.value)} /></F>
          <F label="Teléfono"><Input value={form.telefono} onChange={(e) => setF("telefono", e.target.value)} /></F>
          <F label="Correo"><Input type="email" value={form.correo} onChange={(e) => setF("correo", e.target.value)} /></F>
          <div>
            <Label className="field-label">Estado</Label>
            <Select value={form.estado} onValueChange={(v) => setF("estado", v)}>
              <SelectTrigger data-testid="int-estado"><SelectValue /></SelectTrigger>
              <SelectContent>{ESTADOS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <F label="Tipo de unidad"><TipoUnidadSelect value={form.tipo_unidad} onChange={(v) => setF("tipo_unidad", v)} testId="int-tipo-unidad" /></F>
        </div>
        <div>
          <Label className="field-label">Interés</Label>
          <Textarea rows={3} value={form.interes} onChange={(e) => setF("interes", e.target.value)} />
        </div>
        <div>
          <Label className="field-label">Notas internas</Label>
          <Textarea rows={3} value={form.notas} onChange={(e) => setF("notas", e.target.value)} data-testid="int-notas" />
        </div>
        <DialogFooter>
          <button onClick={onClose} className="btn-outline-dark">Cancelar</button>
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="int-save">
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) { return <div><Label className="field-label">{label}</Label>{children}</div>; }
